/**
 * v3.21-1715 — Admin Ghost / Legacy Reconciliation
 *
 * Operator-safe UI for the three ghost/legacy repair paths:
 *   • Duplicate-client merge  (POST /api/ghost-deal/repair-duplicate)
 *   • Legacy proposal linkage (POST /api/ghost-deal/link-proposal
 *                                and /api/service-auth/reconcile-legacy-proposal)
 *
 * Every action runs dry-run first, shows the projected repointing,
 * and requires explicit admin confirmation before execute.
 * Nothing is emailed, nothing is hard-deleted, everything audited.
 */
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../lib/api';
import { AlertTriangle, GitMerge, Link2, Loader2, CheckCircle2 } from 'lucide-react';
import useDocumentTitle from '../hooks/useDocumentTitle';

export default function AdminGhostReconciliationPage() {
  useDocumentTitle('Admin · Ghost Reconciliation');
  return (
    <div className="space-y-4" data-testid="admin-ghost-reconciliation">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Ghost / Legacy Reconciliation</h1>
        <p className="text-xs text-slate-500">
          Admin-only tools to safely repair test-produced duplicate clients and to
          link historical Timeshare Exit Proposal emails to the authoritative
          Client + Deal — without resending, without hard-deleting.
        </p>
      </div>
      <DuplicateMergeCard />
      <RenumberDealCard />
      <LegacyProposalCard />
    </div>
  );
}

// v3.21-1716 · Existing-Deal Renumber card
function RenumberDealCard() {
  const [dealId,   setDealId]   = useState('');
  const [newNum,   setNewNum]   = useState('');
  const [reason,   setReason]   = useState('GHOST-E2E-002 collision cleanup');
  const [dry,      setDry]      = useState(null);
  const [err,      setErr]      = useState(null);
  const [validating, setValidating] = useState(false);
  const [dryLoading, setDryLoading] = useState(false);
  const [execLoading,setExecLoading]= useState(false);
  const [result,   setResult]   = useState(null);

  const isFormat = (s) => /^PTC-\d{5,}$/.test((s || '').trim().toUpperCase());

  const runValidate = async () => {
    setValidating(true); setErr(null); setDry(null); setResult(null);
    try {
      // Validate = collision + format check via a dry-run.
      const r = await api.post('/ghost-deal/renumber-deal',
        { deal_id: dealId, new_number: newNum, reason, dry_run: true });
      setDry(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setValidating(false); }
  };
  const runDry = runValidate;

  const runExecute = async () => {
    if (!window.confirm(`Execute renumber ${dry?.old_number} → ${dry?.proposed_new}?`)) return;
    setExecLoading(true); setErr(null); setResult(null);
    try {
      const r = await api.post('/ghost-deal/renumber-deal',
        { deal_id: dealId, new_number: newNum, reason, dry_run: false });
      setResult(r.data);
      toast.success(`Renumbered ${r.data.old_number} → ${r.data.new_number}`);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setExecLoading(false); }
  };

  return (
    <Card data-testid="renumber-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <GitMerge className="h-4 w-4 text-cyan-600" />Existing Deal Renumber
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px]">Deal ID (UUID)</Label>
            <Input value={dealId} onChange={e => setDealId(e.target.value)}
                   placeholder="deal_id UUID" className="text-xs h-8"
                   data-testid="renumber-deal-id" />
          </div>
          <div>
            <Label className="text-[11px]">New Deal Number</Label>
            <Input value={newNum} onChange={e => setNewNum(e.target.value.toUpperCase())}
                   placeholder="PTC-00013" className="text-xs h-8"
                   data-testid="renumber-new-number" />
            {newNum && !isFormat(newNum) && (
              <span className="text-[10px] text-red-600">Format: PTC-##### (5+ digits)</span>
            )}
          </div>
        </div>
        <div>
          <Label className="text-[11px]">Reason</Label>
          <Textarea value={reason} onChange={e => setReason(e.target.value)}
                    className="text-xs" data-testid="renumber-reason" />
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-8 text-xs"
                  onClick={runValidate}
                  disabled={validating || !dealId || !isFormat(newNum)}
                  data-testid="renumber-validate">
            {validating ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
            Validate
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs"
                  onClick={runDry}
                  disabled={dryLoading || !dealId || !isFormat(newNum)}
                  data-testid="renumber-dry-run">
            {dryLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <AlertTriangle className="h-3.5 w-3.5 mr-1" />}
            Dry Run
          </Button>
          <Button size="sm" className="h-8 text-xs bg-cyan-600 hover:bg-cyan-700"
                  onClick={runExecute}
                  disabled={execLoading || !dry || dry?.validation?.already_used}
                  data-testid="renumber-execute">
            {execLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
            Execute
          </Button>
        </div>
        {err && (
          <div className="border border-red-200 bg-red-50 rounded p-2 text-[11px] text-red-800" data-testid="renumber-error">
            {typeof err === 'string' ? err : JSON.stringify(err)}
          </div>
        )}
        {dry && (
          <div className="border rounded p-2 bg-slate-50 text-[11px]" data-testid="renumber-dry-result">
            <div>Old: <b>{dry.old_number}</b> → Proposed: <b>{dry.proposed_new}</b></div>
            <div>Validation: format_ok={String(dry.validation?.format_ok)} · already_used={String(dry.validation?.already_used)}
                 {dry.validation?.already_used_in?.length ? ` (${dry.validation.already_used_in.join(', ')})` : ''}</div>
            <div className="mt-1">Affected refs:</div>
            <ul className="pl-4 list-disc">
              {Object.entries(dry.affected || {}).map(([k, v]) => (
                <li key={k}>{k}: {v}</li>
              ))}
            </ul>
          </div>
        )}
        {result && (
          <div className="border rounded p-2 bg-emerald-50 text-[11px] text-emerald-800" data-testid="renumber-exec-result">
            Renumbered <b>{result.old_number}</b> → <b>{result.new_number}</b>. Audit written.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DuplicateMergeCard() {
  const [dup, setDup] = useState('d500bb77-2cd5-4d6f-86b8-f15b21aeb17a');
  const [auth, setAuth] = useState('593ad5c3-1c81-4c33-aa22-3d9d7fbea580');
  const [reason, setReason] = useState('GHOST-E2E-001 test duplicate');
  const [dry, setDry] = useState(null);
  const [dryLoading, setDryLoading] = useState(false);
  const [execLoading, setExecLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runDry = async () => {
    setDryLoading(true); setDry(null); setResult(null);
    try {
      const r = await api.post('/ghost-deal/repair-duplicate', {
        duplicate_client_id: dup, authoritative_client_id: auth,
        reason, dry_run: true,
      });
      setDry(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Dry-run failed'); }
    finally { setDryLoading(false); }
  };
  const execute = async () => {
    setExecLoading(true);
    try {
      const r = await api.post('/ghost-deal/repair-duplicate', {
        duplicate_client_id: dup, authoritative_client_id: auth,
        reason, dry_run: false,
      });
      setResult(r.data);
      toast.success('Duplicate merged — child records repointed idempotently.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Merge failed'); }
    finally { setExecLoading(false); }
  };
  return (
    <Card data-testid="dup-merge-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <GitMerge className="h-4 w-4 text-rose-600" />Duplicate Client Merge
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <Label className="text-[11px]">Duplicate Client UUID</Label>
            <Input value={dup} onChange={e => setDup(e.target.value)} className="font-mono text-[11px] h-8"
                    data-testid="dup-merge-duplicate" />
          </div>
          <div>
            <Label className="text-[11px]">Authoritative Client UUID</Label>
            <Input value={auth} onChange={e => setAuth(e.target.value)} className="font-mono text-[11px] h-8"
                    data-testid="dup-merge-authoritative" />
          </div>
        </div>
        <div>
          <Label className="text-[11px]">Reason</Label>
          <Input value={reason} onChange={e => setReason(e.target.value)} className="text-xs h-8"
                  data-testid="dup-merge-reason" />
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1.5"
                  onClick={runDry} disabled={dryLoading || !dup || !auth}
                  data-testid="dup-merge-dryrun">
            {dryLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <AlertTriangle className="h-3.5 w-3.5" />}
            Dry-Run
          </Button>
          <Button size="sm" className="h-8 text-xs gap-1.5 bg-rose-600 hover:bg-rose-700 text-white"
                  onClick={execute} disabled={!dry || execLoading}
                  data-testid="dup-merge-execute"
                  title={!dry ? 'Run Dry-Run first' : ''}>
            {execLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
            Execute Merge
          </Button>
        </div>
        {dry && (
          <div className="border rounded p-2 bg-slate-50 text-[11px]" data-testid="dup-merge-preview">
            <div className="font-semibold mb-1 text-slate-700">Projected repointing (will not resend anything):</div>
            <div className="grid grid-cols-3 gap-1">
              {Object.entries(dry.would_repoint || {}).map(([coll, n]) => (
                <div key={coll} className="flex items-center justify-between px-1.5 py-0.5 bg-white border rounded">
                  <span className="font-mono">{coll}</span>
                  <Badge variant="outline" className="text-[10px]">{n}</Badge>
                </div>
              ))}
            </div>
          </div>
        )}
        {result && (
          <div className="border rounded p-2 bg-emerald-50 text-[11px] text-emerald-800" data-testid="dup-merge-result">
            Merged {result.merged_client_id?.slice(0,8)}… → {result.authoritative_client_id?.slice(0,8)}…
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function LegacyProposalCard() {
  const [ref, setRef] = useState('PTC-20260902-593AD5C3');
  const [client, setClient] = useState('593ad5c3-1c81-4c33-aa22-3d9d7fbea580');
  const [deal, setDeal] = useState('');
  const [note, setNote] = useState('Historic Timeshare Exit Proposal email — NOT authoritative Service Authorization');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const execute = async () => {
    setLoading(true);
    try {
      const r = await api.post('/service-auth/reconcile-legacy-proposal', {
        proposal_reference: ref, client_id: client, deal_id: deal, note,
      });
      // Also link via ghost-deal link-proposal for legacy proposal collection.
      try {
        await api.post('/ghost-deal/link-proposal', {
          proposal_reference: ref, client_id: client, deal_id: deal,
        });
      } catch { /* ok if ghost-side has no matching row */ }
      setResult(r.data);
      toast.success(`Linked proposal ${ref} → ${deal?.slice(0,8)}…  (no resend)`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reconcile failed'); }
    finally { setLoading(false); }
  };

  return (
    <Card data-testid="legacy-proposal-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Link2 className="h-4 w-4 text-amber-600" />Legacy Proposal Reconciliation
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
          Links a historic <span className="font-mono">Timeshare Exit Proposal</span> email
          (pre-v1715) to the authoritative Client + Deal.  The linked record
          is stamped <span className="font-mono">is_authoritative_service_auth: false</span> so
          it is never mistaken for a real Client Info and Service Authorization.
          No email is resent.  No history is deleted.
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-[11px]">Proposal Reference</Label>
            <Input value={ref} onChange={e => setRef(e.target.value)} className="font-mono text-[11px] h-8"
                    data-testid="legacy-proposal-reference" />
          </div>
          <div>
            <Label className="text-[11px]">Authoritative Client UUID</Label>
            <Input value={client} onChange={e => setClient(e.target.value)} className="font-mono text-[11px] h-8"
                    data-testid="legacy-proposal-client" />
          </div>
          <div className="col-span-2">
            <Label className="text-[11px]">Deal UUID (must exist)</Label>
            <Input value={deal} onChange={e => setDeal(e.target.value)} className="font-mono text-[11px] h-8"
                    placeholder="pick the correct Deal from the workspace"
                    data-testid="legacy-proposal-deal" />
          </div>
        </div>
        <div>
          <Label className="text-[11px]">Reconciliation Note</Label>
          <Textarea value={note} onChange={e => setNote(e.target.value)} className="text-xs" rows={2}
                    data-testid="legacy-proposal-note" />
        </div>
        <Button size="sm" className="h-8 text-xs gap-1.5 bg-amber-600 hover:bg-amber-700 text-white"
                onClick={execute} disabled={loading || !ref || !client || !deal}
                data-testid="legacy-proposal-execute">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
          Link Legacy Proposal (no resend)
        </Button>
        {result && (
          <div className="border rounded p-2 bg-emerald-50 text-[11px] text-emerald-800" data-testid="legacy-proposal-result">
            Matched {result.matched} · Modified {result.modified}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
